/**
 * The tour engine (design: examplebrain note 665e8b13; boards "Tour · official
 * guide step", "Tour · how steps advance", "Assistant · guided panel with
 * highlight · desktop" and "Assistant · guided half-sheet · mobile").
 *
 * Mounted once by the application shell. It holds the official guides this
 * person may use (GET /api/me/guides, fetched on first need), the running tour
 * and what the panel shows of it:
 *
 *   - progress is DERIVED, never recorded (guideTour.js deriveProgress): a
 *     state step is done while the feature's own read says so (every state
 *     step is read again for as long as the tour is open, at a modest interval,
 *     on window focus and just after a click on its control), a visible step
 *     while its observed anchor is on screen and a filled step while its
 *     observed field has a value (both read again from the page itself, so
 *     closing the dialog un-ticks them), a route step while the address is
 *     its route (or a later step is done), a click step once the person
 *     clicked it — while the observed steps before it still hold, or a
 *     later step is done (closing the dialog un-counts its clicks unless
 *     a later step is done). The current step is the first step not done,
 *     so a step that comes undone un-ticks and the tour moves back to it;
 *   - the person opens any step from the panel's list or with Back/Next; that
 *     only changes which control is ringed. A control on another page is
 *     offered as "Go to <page>", an ordinary link the person follows;
 *   - a control the page does not have is first offered to its owner's reveal
 *     (useGuideReveal), then reported; the tour passes it, except a state
 *     step, which the next steps build on: there the tour stops until it is
 *     done or shown.
 *
 * Nothing here clicks, types, submits or navigates for the person. The
 * browser keeps only the running guide and which click steps were clicked
 * (localStorage), so the tour resumes after a reload (Microsoft's consent
 * round trip) when the page matches.
 *
 * The panel, the half-sheet and the highlight are a separate chunk
 * (GuideTourView), loaded only while a tour runs.
 */
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getGuides } from './guidesClient'
import { GUIDE_ANCHOR_ATTRIBUTE, GUIDE_ANCHOR_KEY_ATTRIBUTE } from './guideAnchor'
import { guideStateReader, revealGuideAnchor, subscribeGuideStateReaders } from './guideRegistry'
import {
  anchorSelector,
  clearProgress,
  deriveProgress,
  findAnchorElement,
  followCreated,
  guideAppliesHere,
  isAnchorFilled,
  isAnchorVisible,
  loadActiveProgress,
  locationMatches,
  newItemState,
  newProgress,
  progressFits,
  resolveStep,
  saveProgress,
  stepsSignature,
  storedBaseline,
} from './guideTour'
import './guide-layout.css'

const GuideTourView = lazy(() => import('./GuideTourView'))

/** How long the engine waits: for a control to render, for a reveal to show it, between state reads, and after a click on a state step. */
export const GUIDE_TIMINGS = { find: 4000, reveal: 1500, poll: 5000, recheck: 1200 }

/** Below this width the panel is a half-height bottom sheet over the page. */
export const GUIDE_SHEET_QUERY = '(max-width: 1023.98px)'

const GuideTourContext = createContext(null)

/** The engine's state and actions; null outside the provider (then no guide UI renders). */
export function useGuideTour() {
  return useContext(GuideTourContext)
}

export function prefersReducedMotion() {
  try { return Boolean(globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) } catch { return false }
}

function sheetLayout() {
  try {
    const query = globalThis.matchMedia?.(GUIDE_SHEET_QUERY)
    return query ? query.matches : (globalThis.innerWidth || 1440) < 1024
  } catch {
    return false
  }
}

/** 'panel' (docked beside the page) or 'sheet' (half-height, below the page). */
function useGuideLayout() {
  const [sheet, setSheet] = useState(sheetLayout)
  useEffect(() => {
    const update = () => setSheet(sheetLayout())
    const query = globalThis.matchMedia?.(GUIDE_SHEET_QUERY)
    query?.addEventListener?.('change', update)
    globalThis.addEventListener('resize', update)
    return () => {
      query?.removeEventListener?.('change', update)
      globalThis.removeEventListener('resize', update)
    }
  }, [])
  return sheet ? 'sheet' : 'panel'
}

/** The real error, never a rewritten one: the server's own message and status. */
export function describeGuideError(error) {
  const data = error?.response?.data
  const status = error?.response?.status
  return { message: data?.error || error?.message || String(error), facts: status ? [`HTTP ${status}`] : [] }
}

/** The tour as the provider holds it: what is kept, the guide, and this page's view of it. */
function runningTour(progress, guide, version) {
  return {
    name: guide.name,
    version: version ?? null,
    signature: stepsSignature(guide),
    clicked: Array.isArray(progress?.clicked) ? progress.clicked : [],
    baseline: storedBaseline(progress),
    guide,
    skipped: {},
    viewed: null,
    opened: 0,
  }
}

/**
 * Reads every state step's feature read for as long as the tour is open: at a
 * modest interval while the tab is visible, on window focus, when a feature's
 * readers register, and a moment after a click on a state step's control.
 * Each client function runs once per round, however many steps it answers.
 * A read that fails keeps its steps' last answer (unknown is not undone) and
 * reports the real error.
 *
 * A new-item read's first answer is the tour's starting list (kept with the
 * tour, so a reload keeps it); after that its step is done while another id
 * is listed, and that id is the created item a following step reads.
 */
function useStateReads({ open, steps, timings, unsettle, baseline, keepBaseline }) {
  const [reads, setReads] = useState({ done: {}, errors: {}, ready: false, created: null })
  const recheckRef = useRef(async () => null)
  const doneRef = useRef({})
  const createdRef = useRef(null)
  const unsettleRef = useRef(unsettle)
  unsettleRef.current = unsettle
  // The tour's kept starting lists; a list taken this round waits for the tour to hold it.
  const baselineRef = useRef(baseline)
  baselineRef.current = baseline
  const keepBaselineRef = useRef(keepBaseline)
  keepBaselineRef.current = keepBaseline

  useEffect(() => {
    doneRef.current = {}
    createdRef.current = null
    setReads({ done: {}, errors: {}, ready: false, created: null })
    if (!open) {
      recheckRef.current = async () => null
      return undefined
    }
    const groups = new Map()
    steps.forEach((resolved, index) => {
      if (!resolved.known || resolved.completion.kind !== 'state') return
      const id = `${resolved.read.feature}:${resolved.read.contract}`
      if (!groups.has(id)) groups.set(id, { feature: resolved.read.feature, contract: resolved.read.contract, steps: [] })
      groups.get(id).steps.push({ index, key: resolved.key, read: resolved.read, name: resolved.readName, follows: resolved.follows })
    })
    let cancelled = false
    let running = null
    const round = async () => {
      const done = { ...doneRef.current }
      const errors = {}
      const answers = []
      await Promise.all([...groups].map(async ([id, group]) => {
        const reader = guideStateReader(group.feature, group.contract)
        if (!reader) return
        try {
          const response = await reader()
          answers.push({ group, data: response?.data })
        } catch (error) {
          errors[id] = describeGuideError(error)
        }
      }))
      if (cancelled) return null
      // New-item reads first: a following step reads the item they found.
      let created = createdRef.current
      const taken = {}
      for (const { group, data } of answers) {
        for (const { index, read, name } of group.steps) {
          if (!read.items) continue
          const ids = (read.items(data) || []).map(String)
          const started = baselineRef.current[name] || taken[name]
          if (!started) {
            // A tour starting (again): nothing is created yet.
            taken[name] = ids
            keepBaselineRef.current(name, ids)
            done[index] = false
            created = null
            continue
          }
          const state = newItemState(ids, started, created)
          done[index] = state.done
          created = state.created
        }
      }
      for (const { group, data } of answers) {
        for (const { index, key, read, follows } of group.steps) {
          if (read.items) continue
          if (follows && created === null) done[index] = false
          else done[index] = Boolean(read.done(data, (follows ? created : key) ?? undefined))
        }
      }
      createdRef.current = created
      const before = doneRef.current
      doneRef.current = done
      setReads({ done, errors, ready: true, created })
      // A step that was done and no longer is: what the person clicked after it was for the old state.
      for (const [index, value] of Object.entries(before)) {
        if (value === true && done[index] === false) unsettleRef.current(Number(index))
      }
      return done
    }
    const check = () => {
      if (!running) running = round().finally(() => { running = null })
      return running
    }
    recheckRef.current = check
    check()
    const timer = setInterval(() => {
      if (globalThis.document?.visibilityState !== 'hidden') check()
    }, timings.poll)
    const onFocus = () => { check() }
    globalThis.addEventListener('focus', onFocus)
    const unsubscribe = subscribeGuideStateReaders(() => { check() })
    return () => {
      cancelled = true
      clearInterval(timer)
      globalThis.removeEventListener('focus', onFocus)
      unsubscribe()
      recheckRef.current = async () => null
    }
  }, [open, steps, timings.poll])

  return { reads, recheckRef }
}

/**
 * Reads every visible/filled step's observed anchor from the page itself for
 * as long as the tour is open: on any DOM change (a dialog opening or
 * closing), on any field edit, at the same modest interval as the state
 * reads, on window focus and a moment after a click on its control. Only
 * observations of the page — never a click, a type or a navigation. Closing
 * the dialog un-ticks its observed steps (their controls leave the page);
 * saving ticks them through a later done step (deriveProgress).
 */
function useDomReads({ open, steps, timings, clickedRef, unclick }) {
  const [doms, setDoms] = useState({ done: {}, ready: false })
  const recheckRef = useRef(() => null)
  const doneRef = useRef({})
  const unclickRef = useRef(unclick)
  unclickRef.current = unclick

  // A dialog opening is a fresh choice: forget clicks on controls inside a
  // dialog (the one just opened), so reopening after a cancel asks again.
  // Clicks on page controls, and clicks whose controls are gone (a dialog
  // that closed), stand — whether they still count is derived.
  const clearDialogClicks = useCallback(() => {
    for (const clicked of clickedRef.current || []) {
      const resolved = steps[clicked]
      if (!resolved?.known) continue
      if (findAnchorElement(resolved.anchor, resolved.key)?.closest?.('.modal')) unclickRef.current(clicked)
    }
    // clickedRef is a stable ref: listing it satisfies the rule without
    // ever changing identity (steps already drives resubscription).
  }, [steps, clickedRef])

  useEffect(() => {
    doneRef.current = {}
    setDoms({ done: {}, ready: false })
    if (!open) {
      recheckRef.current = () => null
      return undefined
    }
    const watched = steps
      .map((resolved, index) => ({ index, resolved }))
      .filter(({ resolved }) => resolved.known && (resolved.completion.kind === 'visible' || resolved.completion.kind === 'filled'))
    const round = () => {
      const done = {}
      for (const { index, resolved } of watched) {
        const name = resolved.observed || resolved.anchor
        done[index] = resolved.completion.kind === 'visible'
          ? isAnchorVisible(name, resolved.key)
          : isAnchorFilled(name, resolved.key)
      }
      const before = doneRef.current
      const same = Object.keys(done).length === Object.keys(before).length
        && Object.entries(done).every(([index, value]) => before[index] === value)
      doneRef.current = done
      // The observer below hears the panel's own ticks: only re-render when
      // what the page shows actually changed.
      if (!same) setDoms({ done, ready: true })
      else setDoms((current) => (current.ready ? current : { done, ready: true }))
      // Closing the dialog never deletes clicks: whether a click still counts
      // is derived (deriveProgress), so a close that completed the flow
      // (the dialog saved) keeps them whatever order the page
      // commits the close and the save in. A dialog opening clears
      // them instead: a fresh dialog is a fresh choice. Only an opening
      // counts — a field filling in does not ask again.
      const opened = Object.entries(done).some(([index, value]) => {
        if (value !== true || before[index] === true) return false
        const resolved = steps[Number(index)]
        const name = resolved?.observed || resolved?.anchor
        return findAnchorElement(name, resolved?.key)?.matches?.('.modal') ?? false
      })
      if (opened) clearDialogClicks()
      return done
    }
    recheckRef.current = round
    round()
    const timer = setInterval(() => {
      if (globalThis.document?.visibilityState !== 'hidden') round()
    }, timings.poll)
    const onFocus = () => { round() }
    const onEdit = () => { round() }
    globalThis.addEventListener('focus', onFocus)
    globalThis.document?.addEventListener('input', onEdit, true)
    globalThis.document?.addEventListener('change', onEdit, true)
    const observer = new globalThis.MutationObserver(() => { round() })
    if (globalThis.document?.body) {
      observer.observe(globalThis.document.body, { childList: true, subtree: true, attributes: true })
    }
    return () => {
      clearInterval(timer)
      globalThis.removeEventListener('focus', onFocus)
      globalThis.document?.removeEventListener('input', onEdit, true)
      globalThis.document?.removeEventListener('change', onEdit, true)
      observer.disconnect()
      recheckRef.current = () => null
    }
  }, [open, steps, timings.poll, clearDialogClicks])

  return { doms, domRecheckRef: recheckRef }
}

/**
 * Derives the tour from what is true now and runs the step the panel shows
 * (the current step, or the one the person opened): its control is found,
 * revealed or reported missing, ringed and scrolled into view. Clicks on any
 * step's control are heard (never made).
 */
function useTourEngine({ tour, location, actions, timings }) {
  const open = Boolean(tour)
  const guide = tour?.guide || null
  const declared = useMemo(() => (guide ? guide.steps.map(resolveStep) : []), [guide])
  const { reads, recheckRef } = useStateReads({
    open, steps: declared, timings, unsettle: actions.unsettle, baseline: tour?.baseline || {}, keepBaseline: actions.keepBaseline,
  })
  const steps = useMemo(() => followCreated(declared, reads.created), [declared, reads.created])
  const clickedRef = useRef(tour?.clicked || [])
  clickedRef.current = tour?.clicked || []
  const { doms, domRecheckRef } = useDomReads({ open, steps, timings, clickedRef, unclick: actions.unclick })
  const progress = useMemo(
    () => deriveProgress(steps, { stateDone: reads.done, domDone: doms.done, clicked: tour?.clicked || [], skipped: tour?.skipped || {}, location }),
    [steps, reads.done, doms.done, tour?.clicked, tour?.skipped, location],
  )
  const viewed = tour?.viewed ?? null
  const shown = viewed ?? progress.current
  const resolved = open && shown !== null ? steps[shown] || null : null
  const onPage = Boolean(resolved?.known && resolved.page && locationMatches(resolved.page, location))
  const scrolledRef = useRef(null)
  const shownId = open && shown !== null ? `${tour.name}:${shown}:${tour.opened || 0}` : null
  // The control found for a shown step, kept with that step: while the next
  // step's control is being found, the ring and the scroll never act on the
  // previous step's.
  const [found, setFound] = useState({ element: null, id: null })
  const target = found.id === shownId ? found.element : null
  const readError = resolved?.read ? reads.errors[`${resolved.read.feature}:${resolved.read.contract}`] || null : null

  // The panel follows the tour: when the current step moves (a step got done,
  // or one came undone), or the step the person opened got done.
  const followRef = useRef({ current: undefined, done: [] })
  useEffect(() => {
    const before = followRef.current
    followRef.current = { current: progress.current, done: progress.done }
    if (!open || viewed === null || before.current === undefined) return
    if (before.current !== progress.current || (!before.done[viewed] && progress.done[viewed])) actions.view(null)
  }, [open, viewed, progress, actions])

  // Somewhere else, a control that was not on the last page may be on this one.
  const whereKey = `${location.pathname}?${location.search || ''}`
  const whereRef = useRef(whereKey)
  useEffect(() => {
    if (whereRef.current === whereKey) return
    whereRef.current = whereKey
    actions.clearSkips()
  }, [whereKey, actions])

  // The shown step's control on this page: found by its declared anchor,
  // revealed by its owner when folded away, reported when the page lacks it.
  // Looked up before paint, so the ring and caption land in the same commit
  // as the step they belong to.
  const ready = reads.ready && doms.ready
  useLayoutEffect(() => {
    if (!open || !resolved?.known || !onPage || !ready) {
      setFound({ element: null, id: null })
      return undefined
    }
    const index = shown
    const id = shownId
    let cancelled = false
    let timer = null
    let revealed = false
    let current = null
    const look = () => {
      const element = findAnchorElement(resolved.anchor, resolved.key)
      if (element !== current) {
        current = element
        setFound({ element, id })
        // Shown after all (opened again, or the page rendered it late).
        if (element) actions.skip(index, null)
      }
      if (element && timer) {
        clearTimeout(timer)
        timer = null
      }
      return element
    }
    const missed = async () => {
      timer = null
      if (cancelled || look()) return
      if (!revealed && revealGuideAnchor(resolved.anchor, resolved.key)) {
        revealed = true
        timer = setTimeout(missed, timings.reveal)
        return
      }
      // Done is done wherever the control is: read a state step once more first.
      // A visible/filled step done from the page stays done when its own
      // control is gone (the dialog closed after creating): read it too.
      if (resolved.completion.kind === 'state' && (await recheckRef.current())?.[index] === true) return
      if ((resolved.completion.kind === 'visible' || resolved.completion.kind === 'filled')
        && domRecheckRef.current()?.[index] === true) return
      if (!cancelled && !look()) actions.skip(index, 'missing')
    }
    if (!look()) timer = setTimeout(missed, timings.find)
    const observer = new globalThis.MutationObserver(() => {
      if (!cancelled && !look() && !timer) timer = setTimeout(missed, timings.find)
    })
    observer.observe(globalThis.document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: [GUIDE_ANCHOR_ATTRIBUTE, GUIDE_ANCHOR_KEY_ATTRIBUTE],
    })
    return () => {
      cancelled = true
      observer.disconnect()
      if (timer) clearTimeout(timer)
      setFound({ element: null, id: null })
    }
    // shownId re-runs the look when the person opens the same step again.
  }, [open, resolved, onPage, ready, shown, shownId, actions, recheckRef, domRecheckRef, timings.find, timings.reveal])

  // Bring the control into view once per opened step; focus stays where the person left it.
  useEffect(() => {
    if (!target || scrolledRef.current === shownId) return
    scrolledRef.current = shownId
    target.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }, [target, shownId])

  // The person's own clicks: a click step's control records it; a state
  // step's control asks the reads again a moment later, and a visible/filled
  // step's control asks the page again (the dialog it opens, the field it fills).
  useEffect(() => {
    if (!open) return undefined
    const watched = steps
      .map((step, index) => ({ index, kind: step.known ? step.completion.kind : null, selector: step.known ? anchorSelector(step.anchor, step.key) : null }))
      .filter((entry) => entry.kind === 'click' || entry.kind === 'state' || entry.kind === 'visible' || entry.kind === 'filled')
    if (watched.length === 0) return undefined
    let recheck = null
    let domRecheck = null
    const onClick = (event) => {
      const element = event.target
      if (!element?.closest) return
      let read = false
      let dom = false
      for (const entry of watched) {
        if (!element.closest(entry.selector)) continue
        if (entry.kind === 'click') actions.click(entry.index)
        else if (entry.kind === 'state') read = true
        else dom = true
      }
      if (read) {
        clearTimeout(recheck)
        recheck = setTimeout(() => { recheckRef.current() }, timings.recheck)
      }
      if (dom) {
        clearTimeout(domRecheck)
        domRecheck = setTimeout(() => { domRecheckRef.current() }, timings.recheck)
      }
    }
    globalThis.document.addEventListener('click', onClick, true)
    return () => {
      globalThis.document.removeEventListener('click', onClick, true)
      clearTimeout(recheck)
      clearTimeout(domRecheck)
    }
  }, [open, steps, actions, recheckRef, domRecheckRef, timings.recheck])

  return useMemo(() => ({
    steps,
    done: progress.done,
    reasons: progress.reasons,
    current: progress.current,
    finished: progress.finished,
    stopped: progress.stopped,
    shown,
    resolved,
    onPage,
    target,
    readError,
    ready,
  }), [steps, progress, shown, resolved, onPage, target, readError, ready])
}

export function GuideTourProvider({ children, timings: timingOverrides }) {
  const location = useLocation()
  const layout = useGuideLayout()
  const timings = useMemo(() => ({ ...GUIDE_TIMINGS, ...timingOverrides }), [timingOverrides])
  const [guidesState, setGuidesState] = useState({ status: 'idle', version: null, guides: [], error: null })
  const [tour, setTour] = useState(null)
  const [pending, setPending] = useState(() => loadActiveProgress())
  const [hidden, setHidden] = useState(false)
  const [followUpSlot, setFollowUpSlot] = useState(null)
  const loadingRef = useRef(false)

  const loadGuides = useCallback(() => {
    if (loadingRef.current) return
    loadingRef.current = true
    setGuidesState((current) => ({ ...current, status: current.status === 'ready' ? 'ready' : 'loading', error: null }))
    getGuides()
      .then((response) => {
        const guides = Array.isArray(response?.data?.guides) ? response.data.guides.filter((guide) => guide?.name && Array.isArray(guide.steps)) : []
        setGuidesState({ status: 'ready', version: response?.data?.version ?? null, guides, error: null })
      })
      .catch((error) => {
        loadingRef.current = false
        setGuidesState((current) => ({ ...current, status: 'error', error: describeGuideError(error) }))
      })
  }, [])

  // A tour kept in this browser needs the guides to resume.
  useEffect(() => {
    if (pending) loadGuides()
  }, [pending, loadGuides])

  // Resume on load when the page is one of the guide's pages or one its steps live on.
  useEffect(() => {
    if (!pending || tour || guidesState.status !== 'ready') return
    const guide = guidesState.guides.find((entry) => entry.name === pending.name)
    if (!guide || !progressFits(pending, guide, guidesState.version)) {
      clearProgress(pending.name)
      setPending(null)
      return
    }
    const stepHere = guide.steps.some((step) => {
      const resolved = resolveStep(step)
      return resolved.page && locationMatches(resolved.page, location)
    })
    if (stepHere || guideAppliesHere(guide, location)) {
      setTour(runningTour(pending, guide, guidesState.version))
      setPending(null)
    }
  }, [pending, tour, guidesState, location])

  // Only the running guide and the person's clicks live in this browser.
  useEffect(() => {
    if (tour) saveProgress(tour)
  }, [tour])

  const start = useCallback((guide) => {
    if (!guide?.steps?.length) return
    setPending((current) => {
      if (current && current.name !== guide.name) clearProgress(current.name)
      return null
    })
    setTour((current) => {
      if (current && current.name !== guide.name) clearProgress(current.name)
      return runningTour(newProgress(guide, guidesState.version), guide, guidesState.version)
    })
    setHidden(false)
  }, [guidesState.version])

  const stop = useCallback(() => {
    setTour((current) => {
      if (current) clearProgress(current.name)
      return null
    })
    setHidden(false)
  }, [])

  // What the person and the page tell the engine. None of these completes a
  // step on its own: a click is the person's, the rest only changes the view.
  const actions = useMemo(() => {
    const update = (change) => setTour((current) => (current ? change(current) || current : current))
    return {
      /** The person clicked a click step's control. */
      click: (index) => update((current) => (current.clicked.includes(index) ? null : { ...current, clicked: [...current.clicked, index].sort((x, y) => x - y) })),
      /** Forget one click (a dialog reopened: its controls are fresh choices). */
      unclick: (index) => update((current) => (current.clicked.includes(index) ? { ...current, clicked: current.clicked.filter((entry) => entry !== index) } : null)),
      /** A new-item read's first answer: the ids listed when the tour started. */
      keepBaseline: (name, ids) => update((current) => (current.baseline?.[name]
        ? null
        : { ...current, baseline: { ...current.baseline, [name]: ids } })),
      /** A done state step came undone: clicks after it were for the old state. (Dialog steps never unsettle: closing keeps clicks, reopening clears them.) */
      unsettle: (index) => update((current) => (current.clicked.some((entry) => entry > index)
        ? { ...current, clicked: current.clicked.filter((entry) => entry < index) }
        : null)),
      /** The shown step's control is not on this page (reason), or is after all (null). */
      skip: (index, reason) => update((current) => {
        if ((current.skipped[index] || null) === reason) return null
        const { [index]: _was, ...skipped } = current.skipped
        return { ...current, skipped: reason ? { ...skipped, [index]: reason } : skipped }
      }),
      clearSkips: () => update((current) => (Object.keys(current.skipped).length ? { ...current, skipped: {} } : null)),
      /** Show a step in the panel (null: follow the tour). Never clicks, types or navigates. */
      view: (index) => update((current) => {
        if (index === null) return current.viewed === null ? null : { ...current, viewed: null }
        if (!Number.isInteger(index) || index < 0 || index >= current.guide.steps.length) return null
        return { ...current, viewed: index, opened: (current.opened || 0) + 1 }
      }),
    }
  }, [])

  const engine = useTourEngine({ tour, location, actions, timings })

  // The docked panel narrows the page (nothing is covered); the sheet leaves the page above it.
  const docked = Boolean(tour) && !hidden
  useEffect(() => {
    const root = globalThis.document?.documentElement
    if (!root) return undefined
    if (docked) root.setAttribute('data-guide-dock', layout)
    else root.removeAttribute('data-guide-dock')
    return () => root.removeAttribute('data-guide-dock')
  }, [docked, layout])

  const pageGuides = useMemo(
    () => guidesState.guides.filter((guide) => guide.steps.length > 0 && guideAppliesHere(guide, location)),
    [guidesState.guides, location],
  )

  const value = useMemo(() => ({
    guides: guidesState.guides,
    guidesStatus: guidesState.status,
    guidesError: guidesState.error,
    loadGuides,
    pageGuides,
    pendingName: pending?.name || null,
    tour,
    engine,
    layout,
    hidden,
    setHidden,
    start,
    stop,
    openStep: actions.view,
    followUpSlot,
    setFollowUpSlot,
  }), [guidesState, loadGuides, pageGuides, pending, tour, engine, layout, hidden, start, stop, actions, followUpSlot])

  return (
    <GuideTourContext.Provider value={value}>
      {children}
      {tour && (
        <Suspense fallback={null}>
          <GuideTourView />
        </Suspense>
      )}
    </GuideTourContext.Provider>
  )
}

export default GuideTourProvider
